import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LanguageSelector } from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { Zap, Droplets, ChevronLeft, Shield } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-primary">
                <Zap className="h-5 w-5" strokeWidth={2.5} />
                <Droplets className="h-5 w-5 text-blue-500" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-xl tracking-tight">Safi Energy</span>
            </Link>
            <LanguageSelector variant="ghost" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ChevronLeft className="h-4 w-4" />
          {t('nav_application')}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Shield className="h-8 w-8 text-primary" />
          <h1 className="text-4xl font-bold tracking-tight uppercase">SafiEco Privacy Policy</h1>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Effective Date: February 2024</p>
          
          <p className="text-lg">
            This Privacy Policy explains how Safi Energy, the developer of the SafiEco mobile application, 
            collects, uses, stores, and protects personal data when users access the SafiEco application or related services.
          </p>
          
          <p>
            SafiEco is an application designed to report, monitor, and receive notifications about electricity 
            and water outages in Lubumbashi and other regions of the Democratic Republic of Congo (DRC).
          </p>
          
          <p className="font-semibold text-primary">
            By using the SafiEco application, you agree to the collection and use of information in accordance with this Privacy Policy.
          </p>

          <Section title="1. Legal Compliance">
            <p>Safi Energy operates in compliance with the laws of the Democratic Republic of Congo including:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Ordinance-Law No. 23/010 of 13 March 2023 establishing the Digital Code.</li>
              <li>Telecommunications Framework Law No. 013/2002.</li>
              <li>The Constitution of the Democratic Republic of Congo, which guarantees the privacy of communications.</li>
            </ul>
            <p className="mt-2">Personal data is processed only with the explicit consent of the user or where required by law.</p>
          </Section>

          <Section title="2. Information We Collect">
            <p className="font-medium">Personal Information</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Account credentials</li>
            </ul>
            
            <p className="font-medium mt-4">Location Information</p>
            <ul className="list-disc pl-6">
              <li>GPS or approximate location to identify outages in your area.</li>
            </ul>
            
            <p className="font-medium mt-4">Device Information</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Device model</li>
              <li>Operating system</li>
              <li>IP address</li>
              <li>App usage data</li>
            </ul>
            
            <p className="font-medium mt-4">Service Information</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Outage reports submitted</li>
              <li>Photos or comments related to outages</li>
              <li>Time and location of outage reports</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Data">
            <p>We use collected data for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>To provide electricity and water outage alerts</li>
              <li>To enable community reporting of outages</li>
              <li>To improve infrastructure data accuracy</li>
              <li>To notify users about service restoration</li>
              <li>To improve the SafiEco platform</li>
              <li>To prevent fraud or misuse</li>
              <li>To comply with legal obligations</li>
            </ul>
          </Section>

          <Section title="4. Legal Basis for Processing Data">
            <p>SafiEco processes personal data based on:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>User consent</li>
              <li>Performance of a service requested by the user</li>
              <li>Legal obligations under Congolese law</li>
              <li>Legitimate interests in improving utility monitoring</li>
            </ul>
            <p className="mt-2 font-medium">Users may withdraw consent at any time.</p>
          </Section>

          <Section title="5. Data Sharing">
            <p>SafiEco may share limited data with:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Utility providers (electricity and water companies)</li>
              <li>Local authorities responsible for infrastructure</li>
              <li>Technology service providers supporting the platform</li>
            </ul>
            <p className="mt-2 font-semibold">We do not sell personal data.</p>
            <p>Data may only be shared with government authorities if required by law.</p>
          </Section>

          <Section title="6. Data Retention">
            <p>Safi Energy retains personal data only as long as necessary to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Provide services</li>
              <li>Maintain service security</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p className="mt-2">Usage or connection data may be retained for up to 12 months where required for telecommunications or security compliance.</p>
          </Section>

          <Section title="7. Data Security">
            <p>We implement technical and organizational measures including:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Encryption</li>
              <li>Secure servers</li>
              <li>Access control</li>
              <li>Security monitoring</li>
            </ul>
            <p className="mt-2 text-muted-foreground">However, no digital system can guarantee 100% security.</p>
          </Section>

          <Section title="8. User Rights">
            <p>Under applicable Congolese law, users may:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Request access to their personal data</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of their account</li>
              <li>Withdraw consent for data processing</li>
            </ul>
            <p className="mt-2">Requests can be made by contacting Safi Energy or using our <Link to="/delete-account" className="text-primary hover:underline">account deletion request form</Link>.</p>
          </Section>

          <Section title="9. Children's Privacy">
            <p>SafiEco is not intended for children under 16 years of age.</p>
            <p>We do not knowingly collect personal data from minors.</p>
          </Section>

          <Section title="10. Third-Party Services">
            <p>SafiEco may integrate services such as:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Map providers</li>
              <li>Cloud hosting providers</li>
              <li>Notification services</li>
            </ul>
            <p className="mt-2">These services may collect limited technical data necessary for functionality.</p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>Safi Energy may update this Privacy Policy periodically.</p>
            <p>Users will be notified through the application or via email.</p>
          </Section>

          <Section title="12. Contact Information">
            <p>If you have questions regarding this Privacy Policy, contact:</p>
            <div className="mt-4 p-6 bg-card border border-border rounded-lg">
              <p className="font-semibold text-lg">Safi Energy</p>
              <p className="text-muted-foreground">Lubumbashi, Democratic Republic of Congo</p>
              <p className="text-muted-foreground">Email: info@safienergy.org</p>
              <p className="text-muted-foreground">Website: www.safienergy.org</p>
            </div>
          </Section>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-border bg-card mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">{t('footer_copyright')}</p>
        </div>
      </footer>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mt-8">
    <h2 className="text-2xl font-bold text-foreground mb-4">{title}</h2>
    <div className="text-muted-foreground space-y-2">{children}</div>
  </div>
);
